import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UploadImageButton from '../Components/UploadImageButton';
import './Dashboard.css'; // Reusing Dashboard CSS
import ScheduleTable from '../Components/ScheduleTable';

const SCHEDULE_EVENTS = {
  auger_off: 'Auger Off',
  feed_roll_up: 'Feed Roll Up',
  water_roll_up: 'Water Roll Up',
  catch_time: 'Catch Time',
  process_time: 'Process Time',
};

// One table row per house per event, in chronological order.
export const toScheduleEvents = (rows) =>
  rows
    .flatMap((row) =>
      Object.entries(SCHEDULE_EVENTS).map(([key, label]) => ({
        barn: row.house_number,
        event: label,
        date: row[key]?.value ?? null,
      }))
    )
    .sort((a, b) => new Date(a.date) - new Date(b.date));

function Schedule() {
  const [isLoading, setIsLoading] = useState(true);
  const [sortedData, setSortedData] = useState([]);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchedule();
  }, []);

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  const fetchSchedule = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/schedule');
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      const rows = await response.json();
      setSortedData(toScheduleEvents(rows));
      setUploadedAt(rows[0]?.uploaded_at?.value ?? null);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setError('Could not load the schedule. Try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className="dashboard-container">
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className="top-bar" sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
            <Box></Box> {/* Empty left column for balance */}
            <Typography variant="h5" sx={{ textAlign: 'center' }}>Schedule</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleDashboardClick}
                size="small"
                style={{ fontSize: '0.75rem', padding: '4px 10px' }}
              >
                Dashboard
              </Button>
            </Box>
          </Box>

          <Box className="table-container-schedule">
            {error ? (
              <p>{error}</p>
            ) : (
              <ScheduleTable 
                data={sortedData} 
                headers={["Barn", "Event", "Date"]} 
              />
            )}
          </Box>

          {uploadedAt && (
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
              Schedule uploaded {new Date(uploadedAt).toLocaleString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
              })}
            </Typography>
          )}

          {/* Schedule content with PDF upload */}
          <Box className="upload-button-container">
            <Box>
              <UploadImageButton />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default Schedule;
